import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatINR } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField } from '../../components/ui/Field.jsx';
import { todayIso } from './dateRange.js';

const MODE_LABEL = { cash: 'Cash', upi: 'UPI', card: 'Card', netbanking: 'Net banking', cheque: 'Cheque', old_gold: 'Old gold', chit_adjust: 'Chit adjustment' };

export default function DailySalesPage() {
  const [date, setDate] = useState(todayIso());
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'daily-sales', date],
    queryFn: () => api.get('/reports/daily-sales', { query: { date } }),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/daily-sales', `daily-sales-${date}.csv`, { query: { date } });
    } finally {
      setDownloading(false);
    }
  };

  const invoices = data?.invoices ?? [];
  const cashRow = data?.byMode.find((m) => m.mode === 'cash');
  const grandTotal = invoices.reduce((acc, i) => acc + Number(i.grand_total), 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Daily sales</h1>
          <p className="mt-1 text-sm text-muted">Bill-wise for the day, with payment-mode split for till reconciliation.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {!isLoading ? (
        <section className="card p-5">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Cash reconciliation</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <p className="text-xs text-muted">Bills</p>
              <p className="tabular text-lg font-semibold">{invoices.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Grand total</p>
              <p className="tabular text-lg font-semibold">{formatINR(grandTotal)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Cash received</p>
              <p className="tabular text-lg font-semibold text-primary">{formatINR(cashRow?.totalAmount ?? 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Other modes</p>
              <div className="flex flex-wrap gap-x-3 text-sm">
                {(data?.byMode ?? []).filter((m) => m.mode !== 'cash').map((m) => (
                  <span key={m.mode} className="tabular">{MODE_LABEL[m.mode] ?? m.mode}: {formatINR(m.totalAmount)}</span>
                ))}
                {!(data?.byMode ?? []).some((m) => m.mode !== 'cash') ? <span className="text-muted">None</span> : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <div className="card table-scroll">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">Grand total</th>
              <th className="px-4 py-3 text-right">Paid</th>
              <th className="px-4 py-3 text-right">Balance</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !invoices.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">No invoices on this date.</td></tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.invoice_no} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">{inv.invoice_no}</td>
                  <td className="px-4 py-3">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-right">{formatINR(inv.grand_total)}</td>
                  <td className="px-4 py-3 text-right">{formatINR(inv.paid_amount)}</td>
                  <td className="px-4 py-3 text-right">{Number(inv.balance_amount) > 0 ? <span className="text-warning">{formatINR(inv.balance_amount)}</span> : formatINR(0)}</td>
                  <td className="px-4 py-3 text-muted">{inv.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
